import React, { useRef, useEffect } from "react";
import * as d3 from "d3";
import { rccColors } from "../colors";
import { useRccTypography } from "../typography";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";

const FONT_FAMILY = '"Onest", sans-serif';

// Helper: Format large numbers with commas and K (e.g. 1500000 -> 1,500K)
const kFormatter = (v, currencySymbol = "") => {
  if (v === 0) return "0";
  if (Math.abs(v) < 1000) return `${currencySymbol}${d3.format(",")(v)}`;
  return `${currencySymbol}${d3.format(",.0f")(v / 1000)}K`;
};

// Helper: Format large numbers in millions (e.g. 1500000 -> 1.5M)
const mFormatter = (v, currencySymbol = "") => {
  if (v === 0) return "0";
  if (Math.abs(v) < 1000) return `${currencySymbol}${d3.format(",")(v)}`;
  return `${currencySymbol}${d3.format(",.1f")(v / 1_000_000)}M`;
};

/**
 * Helper to wrap and center SVG text
 * @param {d3.Selection} text - The text selection to wrap
 * @param {number} width - The max width allowed before wrapping
 */
const wrap = (textSelection, width) => {
  textSelection.each(function () {
    const text = d3.select(this);
    const words = text.text().split(/\s+/).reverse();
    let word;
    let line = [];
    const lines = [];

    // 1. Measure and split text into lines
    // Create a temporary tspan to measure widths
    let tspan = text
      .text(null)
      .append("tspan")
      .attr("x", -10)
      .attr("y", 0)
      .style("opacity", 0);

    while ((word = words.pop())) {
      line.push(word);
      tspan.text(line.join(" "));
      if (tspan.node().getComputedTextLength() > width && line.length > 1) {
        line.pop();
        lines.push(line.join(" "));
        line = [word];
        tspan.text(word);
      }
    }
    lines.push(line.join(" "));
    tspan.remove(); // Clean up measurement tspan

    // 2. Render lines centered
    const lineHeight = 1.1; // ems
    const y = text.attr("y") || 0; // Axis text usually has y=0

    // Calculate initial dy to center the entire block vertically
    // 0.35em is the standard center for a single line.
    // For multiple lines, we shift up by half the total height of the extra lines.
    const initialDy = 0.35 - ((lines.length - 1) * lineHeight) / 2;

    lines.forEach((lineText, i) => {
      text
        .append("tspan")
        .attr("x", -10) // Match the axis padding
        .attr("y", y)
        .attr("dy", `${initialDy + i * lineHeight}em`) // First line absolute, others relative
        .text(lineText);
    });
  });
};

/**
 * D3HorizontalDualBar - Configuration-driven horizontal dual bar chart
 * Displays two bars per category: upper bar (Quota Remaining) and lower stacked bar (Won + Qualified Pipeline)
 *
 * @param {Object} props
 * @param {Array} props.data - Chart data array with shape:
 *   [{ team: string, quotaRemaining: number, won: number, qualifiedPipeline: number, percentage: number, multiplier: string }]
 * @param {Object} props.options - Optional configuration
 * @param {string} props.options.currencySymbol - Currency symbol (default: "")
 * @param {boolean} props.options.showValueLabels - Show values on bars (default: true)
 * @param {boolean} props.options.showLegend - Show legend (default: true)
 * @param {number} props.options.marginTop - Top margin (default: 20)
 * @param {number} props.options.marginRight - Right margin (default: 30)
 * @param {number} props.options.marginBottom - Bottom margin (default: 80)
 * @param {number} props.options.marginLeft - Left margin (default: 120)
 * @param {number} props.options.barHeight - Height of each bar row (default: 40)
 * @param {number} props.options.barSpacing - Spacing between upper and lower bars (default: 8)
 */
function D3HorizontalDualBar({ data, options = {} }) {
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const lastDimensions = useRef({ width: 0, height: 0 });
  const typography = useRccTypography();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const colorPalette = rccColors.pipelineCoveragePairedPalette;

  const {
    currencySymbol = "",
    showValueLabels = true,
    showLegend = true,
    marginTop = 10,
    marginRight = 20,
    marginBottom = 50,
    marginLeft = 85, // Slightly increased to give wrapped text breathing room
    barHeight = 40,
    barSpacing = 8,
  } = options;

  const hideQuotaBar = data?.length > 4;

  useEffect(() => {
    if (!wrapperRef.current || !containerRef.current || !data?.length) return;

    const renderChart = () => {
      const wrapper = wrapperRef.current;
      const container = containerRef.current;
      if (!wrapper || !container) return;

      const containerWidth = wrapper.clientWidth;
      const containerHeight = wrapper.clientHeight;

      const width = containerWidth - marginLeft - marginRight;
      const height = containerHeight - marginTop - marginBottom;

      if (width <= 0 || height <= 0) return;

      // Skip if dimensions haven't changed
      const { width: lastW, height: lastH } = lastDimensions.current;
      if (containerWidth === lastW && containerHeight === lastH) return;

      lastDimensions.current = {
        width: containerWidth,
        height: containerHeight,
      };

      // Clear SVG
      d3.select(container).selectAll("*").remove();

      const svg = d3
        .select(container)
        .append("svg")
        .attr("width", containerWidth)
        .attr("height", containerHeight)
        .append("g")
        .attr("transform", `translate(${marginLeft},${marginTop})`);

      // Prepare data - filter out invalid entries
      const validData = data.filter(
        (d) =>
          d.team &&
          (d.quotaRemaining !== undefined ||
            d.won !== undefined ||
            d.qualifiedPipeline !== undefined)
      );

      if (validData.length === 0) return;

      // Calculate max value for X-axis scale
      const maxValue = d3.max(validData, (d) => {
        const quota = d.quotaRemaining || 0;
        const pipeline = (d.won || 0) + (d.qualifiedPipeline || 0);
        return Math.max(quota, pipeline);
      });

      // Auto-scale to millions if values exceed or equal 1,000,000
      const useMillions = maxValue >= 1_000_000;
      const xAxisFormatter = useMillions ? mFormatter : kFormatter;

      // X-axis scale
      const xScale = d3
        .scaleLinear()
        .domain([0, maxValue * 1.1])
        .range([0, width]);

      // Y-axis scale - use full available height
      // Each team gets a band, and within that band we'll place 2 bars (upper + lower)
      const yScalePadding = hideQuotaBar ? 0.04 : 0.15;
      const yScale = d3
        .scaleBand()
        .domain(validData.map((d) => d.team))
        .range([0, height])
        .padding(yScalePadding); // Reduce spacing when quota bar is hidden

      // Colors - using palette
      const quotaColor = colorPalette.quota.color;
      const wonColor = colorPalette.won.color;
      const qualifiedColor = colorPalette.pipeline.color;

      // Text colors for INSIDE bar
      const quotaTextColor = colorPalette.quota.textColor;
      const wonTextColor = colorPalette.won.textColor;
      const pipelineTextColor = colorPalette.pipeline.textColor;

      // Text color for OUTSIDE bar (always neutral dark grey)
      const outsideTextColor = rccColors.neutral[800];

      // Draw bars for each team
      validData.forEach((teamData, index) => {
        const yPos = yScale(teamData.team);
        const quotaRemaining = teamData.quotaRemaining || 0;
        const won = teamData.won || 0;
        const qualifiedPipeline = teamData.qualifiedPipeline || 0;
        const totalPipeline = won + qualifiedPipeline;
        const quotaLineWidth = 3;

        // Calculate band height and position bars within it
        const bandHeight = yScale.bandwidth();
        const barHeightRatio = hideQuotaBar ? 0.7 : 0.48; // Taller bar when quota is hidden
        const gapBetweenBars = hideQuotaBar ? 0 : 2; // No inner gap when only one bar

        // Upper bar: Quota Remaining (positioned in upper portion of band)
        const quotaBarWidth = xScale(quotaRemaining);
        const quotaBarHeight = bandHeight * barHeightRatio;
        const quotaBarY = yPos + bandHeight * 0.05; // 5% from top of band

        if (!hideQuotaBar) {
          svg
            .append("rect")
            .attr("x", 0)
            .attr("y", quotaBarY)
            .attr("width", quotaBarWidth)
            .attr("height", quotaBarHeight)
            .attr("fill", quotaColor);

          // Value label on upper bar - outside first, fallback inside
          if (showValueLabels && quotaRemaining > 0) {
            const labelText = xAxisFormatter(quotaRemaining, currencySymbol);
            const labelY = quotaBarY + quotaBarHeight / 2;

            const tmp = svg
              .append("text")
              .attr("x", 0)
              .attr("y", labelY)
              .attr("dy", "0.35em")
              .attr("text-anchor", "start")
              .attr("class", typography.textExtraSmallSemibold)
              .classed("rcc-bar-label", true)
              .style("opacity", 0)
              .text(labelText);
            const textLen = tmp.node().getComputedTextLength();
            const padding = 12;
            const outsideSpace = width - quotaBarWidth;
            const insideSpace = quotaBarWidth;
            if (outsideSpace >= textLen + padding) {
              tmp
                .attr("x", Math.min(quotaBarWidth + 6, width - 2))
                .attr("text-anchor", "start")
                .style("fill", outsideTextColor)
                .style("opacity", 1);
            } else if (insideSpace >= textLen + padding) {
              tmp
                .attr("x", Math.max(quotaBarWidth - 6, 0))
                .attr("text-anchor", "end")
                .style("fill", quotaTextColor)
                .style("opacity", 1);
            } else {
              tmp.remove();
            }
          }
        }

        // Lower bar: Stacked Won + Qualified Pipeline (positioned in lower portion of band)
        const lowerBarHeight = bandHeight * barHeightRatio;
        const lowerBarY = hideQuotaBar
          ? yPos + (bandHeight - lowerBarHeight) / 2
          : quotaBarY + quotaBarHeight + gapBetweenBars; // Position with fixed gap

        if (hideQuotaBar) {
          svg
            .append("rect")
            .attr("x", 0)
            .attr("y", lowerBarY)
            .attr("width", width)
            .attr("height", lowerBarHeight)
            .attr("fill", rccColors.materialGrey[100]);
        }

        const wonBarWidth = xScale(won);
        const qualifiedBarWidth = xScale(qualifiedPipeline);
        const totalStackedBarWidth = wonBarWidth + qualifiedBarWidth;

        // Won segment (green) - first part of stacked bar
        if (won > 0) {
          // Ensure minimum width for visibility
          const minBarWidth = Math.max(wonBarWidth, 2);
          svg
            .append("rect")
            .attr("x", 0)
            .attr("y", lowerBarY)
            .attr("width", minBarWidth)
            .attr("height", lowerBarHeight)
            .attr("fill", wonColor);

          // Won label - outside first (to the right of stacked bar), fallback inside won segment
          if (showValueLabels) {
            const wonLabelText = `${xAxisFormatter(won, currencySymbol)} (${
              teamData.percentage || 0
            }%)`;
            const wonLabelY = lowerBarY + lowerBarHeight / 2;
            const tmpWon = svg
              .append("text")
              .attr("x", 0)
              .attr("y", wonLabelY)
              .attr("dy", "0.35em")
              .attr("text-anchor", "start")
              .attr("class", typography.textExtraSmallSemibold)
              .classed("rcc-bar-label", true)
              .style("opacity", 0)
              .text(wonLabelText);
            const wonTextLen = tmpWon.node().getComputedTextLength();
            const padding = 12;
            const outsideSpaceWon = width - (wonBarWidth + qualifiedBarWidth);
            const insideSpaceWon = wonBarWidth;
            const qualifiedOutsideSpace =
              width - (wonBarWidth + qualifiedBarWidth);
            const qualifiedCanBeOutside =
              qualifiedOutsideSpace >= wonTextLen + padding;
            if (
              outsideSpaceWon >= wonTextLen + padding &&
              !qualifiedCanBeOutside
            ) {
              tmpWon
                .attr(
                  "x",
                  Math.min(wonBarWidth + qualifiedBarWidth + 6, width - 2)
                )
                .attr("text-anchor", "start")
                .style("fill", outsideTextColor)
                .style("opacity", 1);
            } else if (insideSpaceWon >= wonTextLen + padding) {
              tmpWon
                .attr("x", wonBarWidth / 2)
                .attr("text-anchor", "middle")
                .style("fill", wonTextColor)
                .style("opacity", 1);
            } else {
              tmpWon.remove();
            }
          }
        }

        // Qualified Pipeline segment (blue) - second part of stacked bar, directly adjacent to Won
        if (qualifiedPipeline > 0) {
          svg
            .append("rect")
            .attr("x", wonBarWidth)
            .attr("y", lowerBarY)
            .attr("width", qualifiedBarWidth)
            .attr("height", lowerBarHeight)
            .attr("fill", qualifiedColor);

          // Qualified label - outside first, fallback inside at end of stacked bar
          if (showValueLabels) {
            const qualifiedLabelText = `${xAxisFormatter(
              qualifiedPipeline,
              currencySymbol
            )} (${teamData.multiplier || "0.0"}x)`;
            const labelY = lowerBarY + lowerBarHeight / 2;

            const tmpQ = svg
              .append("text")
              .attr("x", 0)
              .attr("y", labelY)
              .attr("dy", "0.35em")
              .attr("text-anchor", "start")
              .attr("class", typography.textExtraSmallSemibold)
              .classed("rcc-bar-label", true)
              .style("opacity", 0)
              .text(qualifiedLabelText);
            const qTextLen = tmpQ.node().getComputedTextLength();
            const padding = 12;
            const outsideSpaceQ = width - totalStackedBarWidth;
            const insideSpaceQ = qualifiedBarWidth;
            if (outsideSpaceQ >= qTextLen + padding) {
              tmpQ
                .attr("x", Math.min(totalStackedBarWidth + 6, width - 2))
                .attr("text-anchor", "start")
                .style("fill", outsideTextColor)
                .style("opacity", 1);
            } else if (insideSpaceQ >= qTextLen + padding) {
              tmpQ
                .attr("x", Math.max(totalStackedBarWidth - 6, 0))
                .attr("text-anchor", "end")
                .style("fill", pipelineTextColor)
                .style("opacity", 1);
            } else {
              tmpQ.remove();
            }
          }
        }

        // Quota marker line on the stacked bar when quota bar is hidden
        if (hideQuotaBar && quotaRemaining > 0) {
          const quotaX = Math.max(
            0,
            Math.min(xScale(quotaRemaining), width)
          );
          svg
            .append("rect")
            .attr("x", Math.max(quotaX - quotaLineWidth / 2, 0))
            .attr("y", lowerBarY)
            .attr("width", quotaLineWidth)
            .attr("height", lowerBarHeight)
            .attr("fill", quotaColor);

          svg.selectAll(".rcc-bar-label").raise();
        }
      });

      // X-axis - position at bottom of chart area
      const xAxis = d3
        .axisBottom(xScale)
        .ticks(6)
        .tickFormat((d) => xAxisFormatter(d, currencySymbol));

      const xAxisGroup = svg
        .append("g")
        .attr("transform", `translate(0,${height})`)
        .call(xAxis);

      xAxisGroup
        .select(".domain")
        .attr("stroke", rccColors.neutral[100])
        .attr("stroke-width", 1);

      xAxisGroup.selectAll("line").attr("stroke", rccColors.neutral[300]);

      xAxisGroup
        .selectAll("text")
        .attr("class", typography.textExtraSmallRegular)
        .style("fill", rccColors.neutral[800])
        .style("font-family", FONT_FAMILY);

      // Y-axis (team labels)
      const yAxis = d3.axisLeft(yScale);

      const yAxisGroup = svg.append("g").call(yAxis);

      yAxisGroup
        .select(".domain")
        .attr("stroke", rccColors.neutral[100])
        .attr("stroke-width", 1);

      yAxisGroup.selectAll("line").attr("stroke", rccColors.neutral[300]);

      yAxisGroup
        .selectAll("text")
        .attr("class", typography.textExtraSmallRegular)
        .style("fill", rccColors.neutral[800])
        .style("font-family", FONT_FAMILY)
        .attr("x", -10)
        .style("text-anchor", "end")
        // Apply the wrap function here with a width calculated from marginLeft
        .call(wrap, marginLeft - 15);
    };

    renderChart();

    // Handle resize
    const resizeObserver = new ResizeObserver(() => {
      lastDimensions.current = { width: 0, height: 0 };
      renderChart();
    });

    resizeObserver.observe(wrapperRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [
    data,
    currencySymbol,
    showValueLabels,
    marginTop,
    marginRight,
    marginBottom,
    marginLeft,
    barHeight,
    barSpacing,
    typography,
  ]);

  // Legend - Qualified Pipeline, Quota Remaining, Won
  const legendItems = [
    { label: "Qualified Pipeline", color: colorPalette.pipeline.color },
    { label: "Quota Remaining", color: colorPalette.quota.color },
    { label: "Won", color: colorPalette.won.color },
  ];

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        ref={wrapperRef}
        style={{
          width: "100%",
          flex: 1,
          position: "relative",
          minHeight: isMobile ? "300px" : 0,
        }}
      >
        <div
          ref={containerRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: "hidden",
            margin: 0,
            padding: 0,
          }}
        />
      </div>
      {showLegend && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "24px",
            marginTop: "12px",
            flexWrap: "wrap",
            flexShrink: 0,
          }}
        >
          {legendItems.map((item) => (
            <div
              key={item.label}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <div
                style={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  backgroundColor: item.color,
                }}
              />
              <span
                className={typography.textSmallRegular}
                style={{
                  color: rccColors.neutral[800],
                  fontFamily: FONT_FAMILY,
                }}
              >
                {item.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default D3HorizontalDualBar;
